import { AlertTriangleIcon, LoaderCircleIcon, SparklesIcon } from "lucide-react";
import { cn } from "~/lib/utils";
import type { ProjectLeadStatusController, ProjectLeadStatusState } from "./useProjectLeadStatus";

export interface ProjectLeadBadgeProps {
  readonly status: ProjectLeadStatusController;
  readonly onOpenProjectLead: () => void;
}

const STATE_LABELS = {
  draft: "Draft",
  ready: "Ready",
  running: "Running",
  paused: "Paused",
  blocked: "Blocked",
  completed: "Complete",
  cancelled: "Cancelled",
} as const;

function stateLabel(state: ProjectLeadStatusState): string {
  if (state.kind === "loading") return "Checking status";
  if (state.kind === "inactive") return "Not configured";
  if (state.kind === "error") return "Status unavailable";
  return STATE_LABELS[state.summary.controllerState];
}

function stateDotClass(state: ProjectLeadStatusState): string {
  if (state.kind === "active" && state.summary.blockingAttentionCount > 0) return "bg-destructive";
  if (state.kind === "active" && state.summary.controllerState === "running") return "bg-success";
  if (state.kind === "active" && state.summary.controllerState === "paused") return "bg-warning";
  if (state.kind === "error") return "bg-destructive";
  return "bg-muted-foreground/50";
}

function AttentionCount({ state }: { readonly state: ProjectLeadStatusState }) {
  if (state.kind !== "active" || state.summary.openAttentionCount === 0) return null;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 font-medium",
        state.summary.blockingAttentionCount > 0 ? "text-destructive" : "text-warning-foreground",
      )}
    >
      <AlertTriangleIcon className="size-3" aria-hidden />
      {state.summary.openAttentionCount}
    </span>
  );
}

/**
 * Compact Project Lead entry point for the project header. Pure indicator +
 * launcher: lifecycle actions (pause/resume/retry/reconcile) live in
 * ProjectLeadSheet, which this opens via onOpenProjectLead.
 */
export function ProjectLeadBadge({ status, onOpenProjectLead }: ProjectLeadBadgeProps) {
  const stale =
    status.state.kind !== "loading" && status.state.kind !== "error" && status.state.stale;
  return (
    <button
      type="button"
      aria-label="Project Lead status"
      aria-live="polite"
      onClick={onOpenProjectLead}
      className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full border border-border bg-muted px-2.5 text-xs transition-colors hover:bg-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
    >
      <SparklesIcon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
      <span className="hidden font-medium text-foreground sm:inline">Project Lead</span>
      <span
        className={cn("size-1.5 shrink-0 rounded-full", stateDotClass(status.state))}
        aria-hidden
      />
      {/* Living in the global bar, the badge shares its row with the breadcrumb.
          The dot carries the state on its own, so the words step aside first and
          the name follows; the accessible name is unaffected. */}
      <span className="hidden max-w-40 truncate text-muted-foreground md:inline">
        {stateLabel(status.state)}
        {stale ? " · Stale" : ""}
      </span>
      {status.state.kind === "loading" ? (
        <LoaderCircleIcon className="size-3 animate-spin text-muted-foreground" aria-hidden />
      ) : null}
      <AttentionCount state={status.state} />
    </button>
  );
}
